/**
 * Trading Session Highlight primitive for Lightweight Charts v5.
 * Draws colored vertical bands for market sessions (London, Asia, New York, Sydney).
 */

const DEFAULT_SESSIONS = [
  { id: 'asian',   label: 'Asia',     startHour: 0,  endHour: 9,  color: 'rgba(234, 179, 8, 0.06)' },
  { id: 'london',  label: 'London',   startHour: 8,  endHour: 16, color: 'rgba(59, 130, 246, 0.06)' },
  { id: 'newyork', label: 'New York', startHour: 13, endHour: 22, color: 'rgba(239, 68, 68, 0.06)' },
];

class SessionRenderer {
  constructor(data) {
    this._data = data;
  }
  draw() {}
  drawBackground(target) {
    const { bands, showLabels } = this._data;
    if (!bands || bands.length === 0) return;

    target.useBitmapCoordinateSpace((scope) => {
      const ctx = scope.context;
      const hr = scope.horizontalPixelRatio;
      const vr = scope.verticalPixelRatio;
      const h = scope.bitmapSize.height;

      for (const band of bands) {
        // Draw filled rectangle
        ctx.fillStyle = band.color;
        ctx.fillRect(band.x1 * hr, 0, (band.x2 - band.x1) * hr, h);

        // Draw label at top
        if (showLabels && band.label) {
          ctx.fillStyle = band.labelColor || 'rgba(139, 143, 163, 0.35)';
          ctx.font = `${10 * vr}px -apple-system, sans-serif`;
          ctx.textAlign = 'center';
          ctx.fillText(band.label, ((band.x1 + band.x2) / 2) * hr, 14 * vr);
        }
      }
    });
  }
}

class SessionPaneView {
  constructor(source) {
    this._source = source;
    this._data = { bands: [], showLabels: true };
  }

  update() {
    const chart = this._source._chart;
    const candles = this._source._candles;
    const sessions = this._source._sessions;
    const showLabels = this._source._showLabels;

    if (!chart || !candles || candles.length === 0) {
      this._data = { bands: [], showLabels };
      return;
    }

    const timeScale = chart.timeScale();
    const bands = [];

    // Group candles by day, then find session boundaries
    let currentDay = null;
    let dayCandles = [];

    const flushDay = () => {
      if (dayCandles.length === 0) return;

      for (const session of sessions) {
        if (session.hidden) continue;

        // Find candles within this session's hours
        const sessionCandles = dayCandles.filter((c) => {
          // Subtract tzOffset to get real UTC time, then extract hour
          const realUtc = c.time - (this._source._tzOffset || 0) * 3600;
          const date = new Date(realUtc * 1000);
          const hour = date.getUTCHours();
          if (session.startHour < session.endHour) {
            return hour >= session.startHour && hour < session.endHour;
          }
          // Wrap around midnight (e.g., Sydney 22:00 - 07:00)
          return hour >= session.startHour || hour < session.endHour;
        });

        if (sessionCandles.length === 0) continue;

        const firstTime = sessionCandles[0].time;
        const lastTime = sessionCandles[sessionCandles.length - 1].time;

        const x1 = timeScale.timeToCoordinate(firstTime);
        const x2 = timeScale.timeToCoordinate(lastTime);

        if (x1 !== null && x2 !== null) {
          // Extend by half a bar width on each side
          const barWidth = sessionCandles.length > 1
            ? Math.abs(timeScale.timeToCoordinate(sessionCandles[1].time) - x1)
            : 8;
          bands.push({
            x1: x1 - barWidth / 2,
            x2: x2 + barWidth / 2,
            color: session.color,
            label: session.label,
            labelColor: session.labelColor,
          });
        }
      }
    };

    for (const c of candles) {
      const realUtc = c.time - (this._source._tzOffset || 0) * 3600;
      const date = new Date(realUtc * 1000);
      const day = `${date.getUTCFullYear()}-${date.getUTCMonth()}-${date.getUTCDate()}`;

      if (day !== currentDay) {
        flushDay();
        currentDay = day;
        dayCandles = [];
      }
      dayCandles.push(c);
    }
    flushDay();

    this._data = { bands, showLabels };
  }

  renderer() {
    return new SessionRenderer(this._data);
  }

  zOrder() {
    return 'bottom';
  }
}

export class SessionHighlightPrimitive {
  constructor() {
    this._chart = null;
    this._series = null;
    this._candles = [];
    this._sessions = DEFAULT_SESSIONS;
    this._showLabels = true;
    this._tzOffset = 0;
    this._paneView = new SessionPaneView(this);
    this._requestUpdate = null;
  }

  attached({ chart, series, requestUpdate }) {
    this._chart = chart;
    this._series = series;
    this._requestUpdate = requestUpdate;
  }

  detached() {
    this._chart = null;
    this._series = null;
    this._requestUpdate = null;
  }

  paneViews() {
    return [this._paneView];
  }

  updateAllViews() {
    this._paneView.update();
  }

  setData(candles, sessions, showLabels = true, tzOffset = 0) {
    this._candles = candles;
    if (sessions) this._sessions = sessions;
    this._showLabels = showLabels;
    this._tzOffset = tzOffset;
    if (this._requestUpdate) this._requestUpdate();
  }
}

export { DEFAULT_SESSIONS };
